/* eslint-disable import/no-webpack-loader-syntax */
import { getDefaultValue } from '@tencent/data-schema-core';
import dts from '!!raw-loader!./wuji-flow/vue.d.ts';
import { flowApi } from '@/services';
import WujiFlow from './wuji-flow';
import { vue3 } from './vue3-runtime';
import { createFakeStartDetailBlock, getDetailBlockTickId, getEmptyJobFromFlow, getFlowJobPageURL, getNodeSuccessTickId, isTerminalJobStatus, unwrapJobId, } from './wuji-flow-vue-utils';
export async function getTypeDefinitions() {
    return dts;
}
const FLOW_JOB_SESSION_KEY = '@wujibase/wuji-flow/vue:session';
const DEFAULT_POLLING_INTERVAL = 10000;
// 服务器侧异步队列存在状态更新延迟，发起流程/执行审批后额外加速轮询
const FAST_POLLING_INTERVAL = 700;
const FAST_POLLING_DURATION = 5000;
const APPROVAL_INFO_SCHEMA_FIELDS = [
    { id: 'canOperate', type: 'boolean' },
    { id: 'status', type: 'string' },
    { id: 'ctime', type: 'string' },
    { id: 'approvers', type: 'string' },
    { id: 'result', type: 'string' },
    { id: 'form', type: 'object' },
    { id: 'formSchema', type: 'object' },
    { id: 'operateRecords', type: 'array' },
    { id: 'canModifyApprover', type: 'boolean' },
    { id: 'canCancel', type: 'boolean' },
];
function assertVue3Ready() {
    if (!window.Vue3?.Vue3) {
        throw new Error('[@wujibase/wuji-flow/vue] Vue3 not ready, please call composable after Vue3 is loaded at runtime');
    }
}
function toError(err) {
    return err instanceof Error ? err : new Error(String(err));
}
function getStartFormDefaults(flowGraph) {
    return getDefaultValue({
        type: 'object',
        fields: (flowGraph?.params || []).map(field => ({ ...field, required: true })),
    }) || {};
}
function getApprovalInfoDefaults() {
    return getDefaultValue({
        type: 'object',
        fields: APPROVAL_INFO_SCHEMA_FIELDS,
    }) || {
        canOperate: false,
        status: 'not-ready',
        ctime: '',
        approvers: '',
        result: '',
        operateRecords: [],
        canModifyApprover: false,
        canCancel: false,
    };
}
function createJobView(raw, reload) {
    const detailBlocks = raw.id
        ? (raw.detailBlocks || [createFakeStartDetailBlock()])
        : [createFakeStartDetailBlock()];
    const envId = raw.envId || window.w.env || 'dev';
    const branchId = raw.branchId || window.RUNTIME_CONFIGS?.branch || '';
    const projectId = raw.projectId || window.w.projectId;
    return {
        id: raw.id || '',
        title: raw.title || raw.flowName || raw.flowId,
        status: raw.status || '',
        params: raw.jobParams || {},
        state: raw.flowState?.$state || {},
        nodes: raw.flowState?.$nodes || {},
        message: raw.message || '',
        creator: raw.creator || '',
        currentApprovers: raw.currentApprovers || '',
        relatedUsers: raw.relatedUsers || '',
        flowId: raw.flowId,
        ctime: raw.ctime ?? '',
        envId,
        branchId,
        url: getFlowJobPageURL({
            flowId: raw.flowId,
            jobId: raw.id || '',
            envId,
            branchId,
            projectId,
        }),
        detailBlocks,
        jobTickId: raw.jobTickId || '',
        reload,
    };
}
function resolveNodeTickId(session, nodeId, startTickId) {
    return getNodeSuccessTickId({
        startTickId,
        flowState: session.getRawJob()?.flowState,
        nodeId,
    });
}
function requireService(session) {
    const service = session.getService();
    if (!service) {
        throw new Error('[@wujibase/wuji-flow/vue] The current session has no AdvancedFlowService, so nodes cannot be operated');
    }
    return service;
}
async function buildStartNodeView(session, nodeId) {
    const jobId = session.getJobId();
    const rawJob = session.getRawJob();
    const form = rawJob?.jobParams || {};
    return {
        type: 'start',
        id: nodeId,
        form,
        readonly: !!jobId,
        submit: async () => {
            const started = await new WujiFlow().startAdvancedFlow(session.flowId, form);
            return {
                id: started.id,
                url: getFlowJobPageURL({
                    flowId: session.flowId,
                    jobId: started.id,
                    projectId: started.projectId || window.w.projectId,
                    envId: window.w.env,
                    branchId: window.RUNTIME_CONFIGS?.branch || '',
                }),
            };
        },
    };
}
async function buildApprovalNodeView(session, nodeId, startTickId) {
    const jobId = session.getJobId();
    const info = jobId
        ? await requireService(session).fetchApprovalNodeInfo(jobId, nodeId, resolveNodeTickId(session, nodeId, startTickId))
        : getApprovalInfoDefaults();
    const getDefaultForm = () => (info.formSchema && getDefaultValue({
        ...info.formSchema,
        fields: (info.formSchema.fields || []).map(f => ({ ...f, required: true })),
    })) || null;
    const form = info.form || (info.canOperate ? getDefaultForm() : null);
    return {
        type: 'approval',
        id: nodeId,
        ...info,
        form,
        submit: async (operation, comment, form, refresh) => {
            await requireService(session).approve({
                nodeId,
                operation: operation,
                comment,
                form,
            });
            if (refresh !== false)
                await session.reload(true);
            // 刚执行了审批，服务端需要 0.5~1s 才更新状态，加速轮询直到 jobTickId 变化
            session.kickFastPolling();
        },
        modifyApprover: async (approvers) => {
            await requireService(session).modifyApprover(nodeId, approvers);
            await session.reload(true);
        },
        cancelApproval: async (comment) => {
            await requireService(session).cancelApproval(nodeId, comment);
            await session.reload(true);
        },
    };
}
async function buildLogNodeView(session, nodeId, startTickId) {
    const jobId = session.getJobId();
    const rawJob = session.getRawJob();
    const logInfo = {
        title: '',
        message: undefined,
    };
    if (jobId) {
        // const fetched = await requireService(session).fetchLogNodeInfo(
        //   jobId,
        //   nodeId,
        //   resolveNodeTickId(session, nodeId, startTickId),
        // );
        const detailBlocks = rawJob?.detailBlocks?.find(b => b.startTickId === startTickId);
        logInfo.title = detailBlocks?.title || '';
        logInfo.message = detailBlocks?.timeline?.[0]?.message || undefined;
    }
    return {
        type: 'log',
        id: nodeId,
        ...logInfo,
        isRuntime: !!jobId,
    };
}
async function buildNodeView(session, opts) {
    const { nodeId, startTickId } = opts;
    const flowGraph = session.getRawJob()?.flowGraph || session.getRawFlow()?.flowGraph;
    const type = flowGraph?.nodes?.[nodeId]?.type || '';
    if (type === 'start')
        return buildStartNodeView(session, nodeId);
    if (type === 'approval')
        return buildApprovalNodeView(session, nodeId, startTickId);
    if (type === 'log')
        return buildLogNodeView(session, nodeId, startTickId);
    return { id: nodeId, type: type };
}
export function useAdvancedFlowJob(opts) {
    assertVue3Ready();
    const { ref, provide, onScopeDispose, watch } = vue3;
    const job = ref(null);
    const loading = ref(true);
    const error = ref(null);
    const nodeReloads = new Set();
    let rawJob = null;
    let rawFlow = null;
    let service = null;
    let disposed = false;
    let reloadGeneration = 0;
    let readyResolved = false;
    let resolveReady = () => undefined;
    const readyPromise = new Promise((resolve) => {
        resolveReady = resolve;
    });
    const markReady = () => {
        if (readyResolved)
            return;
        readyResolved = true;
        resolveReady();
    };
    const pollingInterval = opts.pollingInterval ?? DEFAULT_POLLING_INTERVAL;
    let timer = null;
    // 快速轮询：流程单刚发起(status=启动)或刚执行审批后，接下来 5s 内持续 500ms 轮询（纯时间窗口，不受 tickId 变化影响）
    let fastPollingUntil = 0;
    const kickFastPolling = () => {
        fastPollingUntil = Date.now() + FAST_POLLING_DURATION;
        schedulePolling();
    };
    const stopPollingTimer = () => {
        if (timer === null)
            return;
        window.clearTimeout(timer);
        timer = null;
    };
    const schedulePolling = () => {
        stopPollingTimer();
        if (disposed || pollingInterval === 0 || !unwrapJobId(opts.jobId))
            return;
        if (document.visibilityState === 'hidden')
            return;
        if (isTerminalJobStatus(rawJob?.jobStatus))
            return;
        const useFast = Date.now() <= fastPollingUntil;
        timer = window.setTimeout(() => {
            timer = null;
            void reload(true, true).finally(schedulePolling);
        }, useFast ? FAST_POLLING_INTERVAL : pollingInterval);
    };
    const onVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
            stopPollingTimer();
            return;
        }
        schedulePolling();
    };
    async function reload(deep = true, noLoadingStatusOverride = false) {
        if (disposed)
            return;
        reloadGeneration += 1;
        const generation = reloadGeneration;
        const jobId = unwrapJobId(opts.jobId);
        if (!noLoadingStatusOverride)
            loading.value = true;
        error.value = null;
        try {
            if (jobId) {
                const nextService = await new WujiFlow().getAdvancedFlowService(jobId);
                if (disposed || generation !== reloadGeneration)
                    return;
                service = nextService;
                rawJob = service.getJobData();
                rawFlow = rawJob;
            }
            else {
                service = null;
                const flow = await flowApi.fetchFlow(window.w.projectId, opts.flowId);
                if (disposed || generation !== reloadGeneration)
                    return;
                if (!flow?.flowGraph)
                    throw new Error(`Invalid flowId: ${opts.flowId}`);
                rawFlow = flow;
                const next = getEmptyJobFromFlow({
                    flowId: opts.flowId,
                    flowGraph: flow.flowGraph,
                    name: flow.name,
                });
                next.jobParams = rawJob?.jobParams || getStartFormDefaults(flow.flowGraph);
                rawJob = next;
            }
            if (generation !== reloadGeneration)
                return;
            if (noLoadingStatusOverride && job.value?.jobTickId === rawJob?.jobTickId)
                return; // 状态没变化，不要强刷
            job.value = createJobView(rawJob, reload);
            // 快速轮询触发：status 仍为「启动」或「队列中」且当前不在加速窗口内时，进入 5s 加速窗口；窗口内纯时间驱动，不因 tickId 变化提前停止
            // i18n ignore next line
            if ((rawJob.status === '启动' || rawJob.status === '队列中') && Date.now() > fastPollingUntil) {
                kickFastPolling();
            }
            // 必须在 deep 子刷新前就绪，否则子节点 await whenReady 会与 Promise.all 互相等待
            markReady();
            if (deep) {
                if (generation !== reloadGeneration)
                    return;
                await Promise.all(Array.from(nodeReloads).map(entry => entry.reload(entry.getNewestTickId())));
            }
        }
        catch (err) {
            if (disposed || generation !== reloadGeneration)
                return;
            error.value = toError(err);
            throw error.value;
        }
        finally {
            if (generation === reloadGeneration) {
                loading.value = false;
                markReady();
                schedulePolling();
            }
        }
    }
    const session = {
        flowId: opts.flowId,
        getJobId: () => unwrapJobId(opts.jobId),
        getRawJob: () => rawJob,
        getRawFlow: () => rawFlow,
        getService: () => service,
        whenReady: () => readyPromise,
        reload,
        kickFastPolling,
        registerNodeReload: (entry) => {
            nodeReloads.add(entry);
        },
        unregisterNodeReload: (reload) => {
            for (const entry of nodeReloads) {
                if (entry.reload !== reload)
                    continue;
                nodeReloads.delete(entry);
                return;
            }
        },
    };
    provide(FLOW_JOB_SESSION_KEY, session);
    if (pollingInterval !== 0) {
        document.addEventListener('visibilitychange', onVisibilityChange);
    }
    watch(() => unwrapJobId(opts.jobId), (next, prev) => {
        if (next === prev || disposed)
            return;
        void reload(true).catch(() => undefined);
    });
    void reload(true).catch(() => undefined);
    onScopeDispose(() => {
        disposed = true;
        stopPollingTimer();
        document.removeEventListener('visibilitychange', onVisibilityChange);
        nodeReloads.clear();
    });
    return { job, loading, error, reload, injectedSession: session };
}
export function useAdvancedFlowJobNode(opts) {
    assertVue3Ready();
    const { ref, inject, onScopeDispose } = vue3;
    const injected = opts.injectedSession || inject(FLOW_JOB_SESSION_KEY);
    if (!injected) {
        throw new Error('[@wujibase/wuji-flow/vue] useAdvancedFlowJobNode cannot find a flowJob session. Please call useAdvancedFlowJob on the parent component first');
    }
    const session = injected;
    const node = ref(null);
    const loading = ref(true);
    const error = ref(null);
    let nodeDisposed = false;
    let loaded = false;
    let lastTickId;
    let inflight = null;
    let inflightTickId;
    const getNewestTickId = () => getDetailBlockTickId(session.getRawJob()?.detailBlocks, opts.nodeId, opts.startTickId);
    async function reload(newestTickId) {
        if (nodeDisposed)
            return;
        const fromJob = newestTickId !== undefined;
        const tickId = fromJob ? newestTickId : getNewestTickId();
        if (fromJob && loaded && lastTickId === tickId)
            return;
        if (inflight) {
            if (inflightTickId === tickId)
                return inflight;
            // 进行中的请求对应旧 tickId，结果可能已过时；等它结束后按新 tickId 再判断一次
            await inflight.catch(() => undefined);
            return reload(newestTickId);
        }
        loading.value = true;
        error.value = null;
        inflightTickId = tickId;
        inflight = (async () => {
            try {
                await session.whenReady();
                if (nodeDisposed)
                    return;
                const next = await buildNodeView(session, opts);
                if (nodeDisposed)
                    return;
                node.value = next;
                loaded = true;
                lastTickId = tickId;
            }
            catch (err) {
                if (nodeDisposed)
                    return;
                error.value = toError(err);
                throw error.value;
            }
            finally {
                inflight = null;
                if (!nodeDisposed)
                    loading.value = false;
            }
        })();
        return inflight;
    }
    session.registerNodeReload({ getNewestTickId, reload });
    onScopeDispose(() => {
        nodeDisposed = true;
        session.unregisterNodeReload(reload);
    });
    void reload().catch(() => undefined);
    return { node, loading, error, reload };
}
